import { useEffect, useState } from "react";
import axios from 'axios';
import styled from "styled-components";
import fundo from "../../../../styles/fundo.svg";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaDownload } from "react-icons/fa";
import { FiZoomIn, FiZoomOut } from "react-icons/fi";


import logo1 from "./logo-1.png";
import logo6 from "./logo-6.png";


// ========== LOOK & FEEL MINIMALISTA ==========
const palette = {
  // fundo da página (se usar)
  cardBg: "#ffffff",
  headBg: "#0a7a3f",          // cabeçalho da tabela
  headText: "#ffffff",
  text: "#334155",
  textMuted: "#64748b",
  border: "#e5e7eb",
  zebra: "#f8fafc",

  accent: "#0ea5e9",
};

// Container do card/tabela
const Geral = styled.div`
  overflow: hidden;
  background: ${palette.cardBg};
  border: 1px solid ${palette.border};
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  margin: 10px auto;
  width: 100%;

  /* Envolve a tabela num scroller vertical suave */
  & > div {
    overflow: auto;
  }

  /* Scrollbar minimal */
  & > div::-webkit-scrollbar {
    height: 10px;
    width: 10px;
  }
  & > div::-webkit-scrollbar-track {
    background: transparent;
  }
  & > div::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 999px;
  }
  & > div::-webkit-scrollbar-thumb:hover {
    background: #c4c9d1;
  }

  @media (max-width: 1367px) {
    margin: 8px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  }

  @media (max-width: 1024px) {
    margin: 8px;
    margin-bottom: 100px;
  }
`;

// Tabela
const Table = styled.table`
  border-collapse: collapse;   /* 👈 junto com as bordas fica linear */
  width: 100%;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto,
    "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif;


  /* borda inferior de cada linha do corpo */
  tbody tr:not(:last-child) td {
    border-bottom: 1px solid ${palette.border};
  }
`;

// Cabeçalho sticky com fallback de cor
const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: ${palette.headBg};
  background-blend-mode: multiply;
  backdrop-filter: saturate(120%) blur(2px);
`;

// Linhas
export const Tr = styled.tr`
  /* ❌ remova esta linha:
  border-bottom: 1px solid ${palette.border};
  */

  ${({ noHover }) =>
    !noHover &&
    `
    &:hover {
      background-color: ${palette.zebra}; /* ou outra cor de hover */
    }
  `}

  @media (max-width: 1024px) {
    .hide-on-mobile {
      display: none;
    }
  }
`;

// Cabeçalho das colunas
export const Th = styled.th`
  color: ${palette.headText};
  padding: clamp(8px, 1.6vw, 14px) clamp(6px, 1.4vw, 10px);
  font-size: clamp(8px, 1.2vw, 11px);
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  white-space: nowrap;

  &.hide-on-mobile {
    @media (max-width: 1024px) {
      display: none;
    }
  }

  .tooltip {
    position: relative;
    cursor: help;
  }
  .tooltip .tooltiptext {
    visibility: hidden;
    width: max-content;
    background-color: #111827;
    color: #fff;
    text-align: left;
    border-radius: 8px;
    padding: 4px 8px;
    position: absolute;
    z-index: 10;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity .2s ease;
    white-space: nowrap;
    box-shadow: 0 8px 20px rgba(0,0,0,.2);
  }
  .tooltip:hover .tooltiptext {
    visibility: visible;
    opacity: 1;
  }
`;

// Células
export const Td = styled.td`
  padding: clamp(10px, 1.8vw, 16px) clamp(6px, 1.4vw, 10px);
  text-align: ${(props) => (props.alignCenter ? 'center' : 'left')};
  font-size: clamp(8px, 1.4vw, 11px);
  color: ${palette.text};
  background-color: ${(props) => (props.zebra ? palette.zebra : "transparent")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.mobile-hide {
    @media (max-width: 1024px) {
      display: none;
    }
  }
`;

// Corpo
export const Tbody = styled.tbody`
  background-color: #fff;
`;

// TH que funciona como “cabeçalho-título” (linha 1)
export const CabecalhoTabela = styled.th`
  text-align: center;
  padding: clamp(10px, 2vw, 18px);
  color: ${palette.headText};
  background-color: ${palette.headBg};
  position: sticky;
  top: 0;
  z-index: 6; /* acima do Thead */
  border-bottom: 1px solid rgba(255,255,255,0.08);

  h2 {
    margin: 0;
    font-size: clamp(14px, 2.4vw, 18px);
    font-weight: 700;
    letter-spacing: 0.01em;
  }
`;

// Botão “fantasma” no header (usado para ícones)
const Button = styled.td`
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background-color: transparent;
  color: ${palette.headText};
  opacity: .9;
  transition: transform .12s ease, opacity .12s ease;
  user-select: none;

  &:hover { transform: translateY(-1px); opacity: 1; }
  &:active { transform: translateY(0); opacity: .85; }

  @media (max-width: 780px) {
    font-size: 14px;
  }
`;

// Header do título/ações
const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: 780px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;

    h2 { font-size: clamp(16px, 5vw, 20px); }
  }
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  p {
    margin: 0;
    font-size: 12px;
    color: ${palette.headText};
    opacity: .85;
  }

  @media (max-width: 780px) {
    width: 100%;
    justify-content: space-between;
    p { font-size: 11px; }
  }
`;

const IconGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  /* alvo o <td> Button que contém ícones */
  ${Button} {
    padding: 6px 8px;
    border-radius: 10px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
  }
`;



const useBoletim = (tipo, urlApi, rota = "atualiza-20-minutos") => {
  const [dados, setDados] = useState([]);

  useEffect(() => {
    const fetchDados = async () => {
      try {
        const res = await axios.get(`${urlApi}boletins-externo/${rota}/${tipo}`);
        setDados(res.data);
      } catch (error) {
        console.error(`Erro ao buscar ${tipo}:`, error);
      }
    };

    fetchDados();
  }, [tipo, urlApi, rota]);

  return dados;
};
const Grid = ({ urlApi, zoomIn, zoomOut }) => {



  //=========================== Atualiza a cada 20 minutos ==============================//
  const dataCabecalho = useBoletim("data-cabecalho", urlApi);
  const entradaDeCana = useBoletim("entrada-de-cana", urlApi);

  const tchGeral = useBoletim("tch-geral", urlApi);
  const tchCooperval = useBoletim("tch-cooperval", urlApi);
  const tchAgroenergia = useBoletim("tch-agroenergia", urlApi);
  const tchCooperado = useBoletim("tch-cooperado", urlApi);
  const tchParceiro = useBoletim("tch-parceiro", urlApi);

  const tahGeral = useBoletim("tah-geral", urlApi);
  const tahCooperval = useBoletim("tah-cooperval", urlApi);
  const tahAgroenergia = useBoletim("tah-agroenergia", urlApi);
  const tahCooperado = useBoletim("tah-cooperado", urlApi);
  const tahParceiro = useBoletim("tah-parceiro", urlApi);
  const horaQueRodou = useBoletim("hora-rodou", urlApi);
  const datas = entradaDeCana.map((item) => item.DATA);



  //=========================== Atualiza 2 vezes ao dia ==============================//
  const dataCabecalho2vezesDia = useBoletim("data-cabecalho", urlApi, "atualiza-2-vezes-dia");
  const semanaZerada2vezesDia = useBoletim("semana-zerada", urlApi, "atualiza-2-vezes-dia");
  const entradaDeCana2vezesDia = useBoletim("entrada-de-cana", urlApi, "atualiza-2-vezes-dia");









  //=======================JUNTA OS MAP======================================//
  const LinhaTabela = ({ titulo, dados1, dados2 }) => (
    <Tr>
      <Td><strong>{titulo}</strong></Td>
      {dados1.map((item, index) => (
        <Td key={`1-${index}`} alignCenter zebra={index % 2 === 0} className="mobile-hide">
          {item.VALOR != null && item.VALOR !== 0
            ? item.VALOR.toLocaleString("pt-BR", { minimumFractionDigits: 2 })
            : "-"}
        </Td>
      ))}
      {dados2.map((item, index) => (
        <Td key={`2-${index}`} alignCenter zebra={index % 2 === 0}>
          {item.VALOR != null && item.VALOR !== 0
            ? item.VALOR.toLocaleString("pt-BR", { minimumFractionDigits: 2 })
            : "-"}
        </Td>
      ))}
    </Tr>
  );





  //=======================GERAR PDF==========================================//

  const getBase64FromImage = (imgSrc) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve({ base64: canvas.toDataURL("image/png"), width: img.naturalWidth, height: img.naturalHeight });
      };

      img.onerror = () => reject(new Error("Erro ao carregar imagem"));
      img.src = imgSrc;
    });
  };



  const gerarPdfRelatorio = async () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "A4" });

    // Marca d'água (logo1 sempre)
    const { base64: marcaDaguaBase64, width: mw, height: mh } = await getBase64FromImage(logo1);
    const maxMarcaW = 400;
    const maxMarcaH = 300;
    let marcaW = mw;
    let marcaH = mh;
    if (mw > maxMarcaW || mh > maxMarcaH) {
      const ratio = Math.min(maxMarcaW / mw, maxMarcaH / mh);
      marcaW = mw * ratio;
      marcaH = mh * ratio;
    }

    const centerX = (842 - marcaW) / 2;
    const centerY = (595 - marcaH) / 2;

    // Cabeçalho fixo (exibição do logo aleatório etc)
    const { base64, width, height } = await await getBase64FromImage(logo6);
    let displayW = width, displayH = height;
    const maxW = 200, maxH = 60;
    if (width > maxW || height > maxH) {
      const ratio = Math.min(maxW / width, maxH / height);
      displayW = width * ratio;
      displayH = height * ratio;
    }

    doc.addImage(base64, 'PNG', 20, 10, displayW, displayH);
    doc.setFontSize(12);
    doc.setTextColor(87, 87, 87);
    doc.text("Boletim Produtividade", 30, 70);
    doc.text(`Última atualização: ${horaQueRodou.hora}`, 600, 70);

    // Cabeçalhos e dados da tabela
    const headers = ["Período"];
    dataCabecalho2vezesDia.forEach(d => headers.push(d.DATA));
    dataCabecalho.forEach(d => headers.push(d.DATA));

    const body = [];
    const montarLinha = (titulo, dados1, dados2) => {
      const linha = [titulo];
      dados1.forEach(item =>
        linha.push(item?.VALOR != 0 ? item.VALOR.toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "-")
      );
      dados2.forEach(item =>
        linha.push(item?.VALOR != 0 ? item.VALOR.toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "-")
      );
      body.push(linha);
    };

    montarLinha("TCH Geral (ton/ha)", semanaZerada2vezesDia, tchGeral);
    montarLinha("TCH Cooperval (ton/ha)", semanaZerada2vezesDia, tchCooperval);
    montarLinha("TCH Agroenergia (ton/ha)", semanaZerada2vezesDia, tchAgroenergia);
    montarLinha("TCH Cooperado (ton/ha)", semanaZerada2vezesDia, tchCooperado);
    montarLinha("TCH Parceiro (ton/ha)", semanaZerada2vezesDia, tchParceiro);

    montarLinha("TAH Geral (ton/ha)", semanaZerada2vezesDia, tahGeral);
    montarLinha("TAH Cooperval (ton/ha)", semanaZerada2vezesDia, tahCooperval);
    montarLinha("TAH Agroenergia (ton/ha)", semanaZerada2vezesDia, tahAgroenergia);
    montarLinha("TAH Cooperado (ton/ha)", semanaZerada2vezesDia, tahCooperado);
    montarLinha("TAH Parceiro (ton/ha)", semanaZerada2vezesDia, tahParceiro);

    autoTable(doc, {
      head: [headers],
      body: body,
      startY: 80,
      margin: { top: 50, left: 20, right: 20 },
      styles: {
        fontSize: 10,
        cellPadding: 4
      },
      headStyles: {
        fillColor: [7, 99, 51],
        textColor: 255
      },
      didDrawPage: (data) => {
        doc.setGState(new doc.GState({ opacity: 0.08 })); // leve opacidade
        doc.addImage(marcaDaguaBase64, 'JPEG', centerX, centerY, marcaW, marcaH);
        doc.setGState(new doc.GState({ opacity: 1 }));
      }
    });

    doc.save(`boletim_produtividade_${new Date().toLocaleString("pt-BR")}.pdf`);
  };
  //===========================TELA CHEIA==============================//
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.log(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };


  return (
    <Geral>

      {horaQueRodou != null && (
        <>


          <Table>
            <Thead>
              <Tr noHover>
                <CabecalhoTabela colSpan={13}>
                  <HeaderWrapper>
                    <h2 >Produtividade</h2>
                    <HeaderRight>
                      <p >Atualizado: {horaQueRodou.hora}</p>
                      <IconGroup>
                        <Button onClick={gerarPdfRelatorio} placeholder="Imprimir">
                          <FaDownload />
                        </Button>
                        <Button
                          onClick={zoomOut}
                          title="Diminuir zoom (Alt+-)"
                          aria-label="Diminuir zoom"
                          type="button"
                        >
                          <FiZoomOut />
                        </Button>
                        <Button
                          onClick={zoomIn}
                          title="Aumentar zoom (Alt+=)"
                          aria-label="Aumentar zoom"
                          type="button"
                        >
                          <FiZoomIn />
                        </Button>
                      </IconGroup>
                    </HeaderRight>
                  </HeaderWrapper>
                </CabecalhoTabela>
              </Tr>

              <Tr noHover>
                <Th width="22%" >Período:</Th>
                {dataCabecalho2vezesDia.map((item, index) => (
                  <Th key={index} className="hide-on-mobile">
                    <div className="tooltip">
                      {item.DATA}
                      <span className="tooltiptext">{item.PERIODO}</span>
                    </div>
                  </Th>
                ))}

                {dataCabecalho.map((item, index) => (
                  <Th key={index} >
                    <div className="tooltip">
                      {item.DATA}
                      {item.PERIODO && (
                        <span className="tooltiptext">{item.PERIODO}</span>
                      )}
                    </div>
                  </Th>
                ))}


              </Tr>
            </Thead>


            <Tbody>
              <LinhaTabela titulo="TCH Geral (ton/ha)" dados1={semanaZerada2vezesDia} dados2={tchGeral} />
              <LinhaTabela titulo="TCH Cooperval (ton/ha)" dados1={semanaZerada2vezesDia} dados2={tchCooperval} />
              <LinhaTabela titulo="TCH Agroenergia (ton/ha)" dados1={semanaZerada2vezesDia} dados2={tchAgroenergia} />
              <LinhaTabela titulo="TCH Cooperado (ton/ha)" dados1={semanaZerada2vezesDia} dados2={tchCooperado} />
              <LinhaTabela titulo="TCH Parceiro (ton/ha)" dados1={semanaZerada2vezesDia} dados2={tchParceiro} />

              <LinhaTabela titulo="TAH Geral (ton/ha)" dados1={semanaZerada2vezesDia} dados2={tahGeral} />
              <LinhaTabela titulo="TAH Cooperval (ton/ha)" dados1={semanaZerada2vezesDia} dados2={tahCooperval} />
              <LinhaTabela titulo="TAH Agroenergia (ton/ha)" dados1={semanaZerada2vezesDia} dados2={tahAgroenergia} />
              <LinhaTabela titulo="TAH Cooperado (ton/ha)" dados1={semanaZerada2vezesDia} dados2={tahCooperado} />
              <LinhaTabela titulo="TAH Parceiro (ton/ha)" dados1={semanaZerada2vezesDia} dados2={tahParceiro} />

            </Tbody>


          </Table>

        </>

      )}
    </Geral>
  );
};



export default Grid;
